import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import logoWhite from '../../assets/images/invertLogoWhite.png';
import Button from '../../design-system/Button';

const SECTION_LINKS = [
  { label: 'What You Get', target: 'what-you-get' },
  { label: 'Why It Works', target: 'why' },
  { label: 'Pricing', target: 'pricing' },
  { label: 'FAQ', target: 'faq' },
];

export function scrollToSection(id) {
  const el = document.getElementById(id);
  if (el) {
    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }
}

function AdvertiserNav() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    if (!mobileOpen) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [mobileOpen]);

  const handleNav = (target) => {
    setMobileOpen(false);
    scrollToSection(target);
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-[1000] transition-colors duration-300 pt-[env(safe-area-inset-top)] ${
        scrolled
          ? 'bg-invert-navy/95 backdrop-blur-xl border-b border-white/10'
          : 'bg-transparent border-b border-transparent'
      }`}
    >
      <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-14 sm:h-16 lg:h-20 flex items-center justify-between gap-3">
        <button
          type="button"
          onClick={() => scrollToSection('top')}
          className="flex items-center gap-2 shrink-0 min-w-0 bg-transparent border-0 cursor-pointer p-0"
          aria-label="Back to top"
        >
          <img src={logoWhite} alt="INVERT" className="h-7 sm:h-8 lg:h-9 w-auto" />
        </button>

        <div className="hidden lg:flex items-center gap-8">
          {SECTION_LINKS.map((link) => (
            <button
              key={link.target}
              type="button"
              onClick={() => handleNav(link.target)}
              className="text-sm font-medium text-slate-300 hover:text-white transition-colors bg-transparent border-0 cursor-pointer p-0"
            >
              {link.label}
            </button>
          ))}
        </div>

        <div className="hidden lg:flex items-center gap-3">
          <Button to="/advertiserLogin/" variant="ghost" size="sm" className="text-slate-300 hover:text-white">
            Advertiser Login
          </Button>
          <Button variant="primary" size="sm" onClick={() => scrollToSection('inquiry')}>
            Get Pricing
          </Button>
        </div>

        <div className="flex lg:hidden items-center gap-2 shrink-0">
          <Button variant="primary" size="sm" className="min-h-[40px] px-3.5 whitespace-nowrap" onClick={() => scrollToSection('inquiry')}>
            Inquire
          </Button>
          <button
            type="button"
            className="min-h-[40px] min-w-[40px] flex items-center justify-center rounded-lg text-white hover:bg-white/10"
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={mobileOpen}
          >
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
              {mobileOpen ? (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              )}
            </svg>
          </button>
        </div>
      </nav>

      {mobileOpen && (
        <>
          <button
            type="button"
            className="lg:hidden fixed inset-0 top-[calc(3.5rem+env(safe-area-inset-top))] sm:top-[calc(4rem+env(safe-area-inset-top))] bg-black/50 z-[999]"
            aria-label="Close menu"
            onClick={() => setMobileOpen(false)}
          />
          <div className="lg:hidden relative z-[1001] border-t bg-invert-navy border-white/10 max-h-[calc(100dvh-3.5rem-env(safe-area-inset-top))] sm:max-h-[calc(100dvh-4rem-env(safe-area-inset-top))] overflow-y-auto">
            <div className="px-4 sm:px-6 py-4 flex flex-col gap-1 pb-[calc(1rem+env(safe-area-inset-bottom))]">
              {SECTION_LINKS.map((link) => (
                <button
                  key={link.target}
                  type="button"
                  onClick={() => handleNav(link.target)}
                  className="py-3.5 px-1 text-base font-medium min-h-[44px] flex items-center text-white active:bg-white/5 rounded-lg bg-transparent border-0 cursor-pointer text-left"
                >
                  {link.label}
                </button>
              ))}
              <div className="pt-4 flex flex-col gap-3 border-t border-white/10 mt-2">
                <Button variant="primary" size="md" className="w-full min-h-[48px]" onClick={() => handleNav('inquiry')}>
                  Get Pricing
                </Button>
                <Button to="/advertiserLogin/" variant="outline" size="md" className="w-full min-h-[48px] text-white border-white/20">
                  Advertiser Login
                </Button>
              </div>
            </div>
          </div>
        </>
      )}
    </header>
  );
}

function AdvertiserFooter() {
  return (
    <footer className="bg-invert-navy text-white border-t border-white/5">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-14 lg:py-16">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-8">
          <div className="max-w-md">
            <img src={logoWhite} alt="INVERT" className="h-8 w-auto mb-4" />
            <p className="text-slate-400 text-sm leading-relaxed">
              The hyper-local advertising network. Reach real customers on real screens in the places where they eat, shop, and gather.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 shrink-0">
            <Button variant="primary" size="lg" onClick={() => scrollToSection('inquiry')}>
              Get Pricing
            </Button>
            <Button to="/" variant="secondary" size="lg">
              Visit INVERT.com
            </Button>
          </div>
        </div>
        <div className="mt-12 pt-8 border-t border-white/10 flex flex-col sm:flex-row justify-between items-center gap-4">
          <p className="text-sm text-slate-500">
            &copy; {new Date().getFullYear()} INVERT. All rights reserved.
          </p>
          <div className="flex items-center gap-6">
            <Link to="/tos" className="text-sm text-slate-500 hover:text-white transition-colors">
              Terms of Service
            </Link>
            <Link to="/contactUs" className="text-sm text-slate-500 hover:text-white transition-colors">
              Contact
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
}

export default function AdvertiserLandingLayout({ children }) {
  return (
    <div className="min-h-screen bg-white">
      <AdvertiserNav />
      <main id="top">{children}</main>
      <AdvertiserFooter />
    </div>
  );
}
